<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import moment from "moment-timezone"
import { formatDate, formatIdSource } from "@core/helpers"
import { METER_TYPES_LIST, SOURCES_TYPES, TASK_STATE } from "@core/constants"
import { useAuthStore } from "@core/stores/auth.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useSourceStore } from "@core/stores/source.store"
import { useTaskStore } from "@core/stores/task.store"
import { useInterrupteursStore } from "@core/stores/interrupteurs.store"
import { track } from "@core/tracking/track"
import type { OptimSegment } from "@core/tracking/events"
import { useOptimTurpeDownloadReport } from "@modules/courtage/composables/useOptimTurpeDownloadReport"
import { useOptimTurpeStore } from "@modules/courtage/stores/optim-turpe.store"
import { getSourceSegmentForOptim } from "@modules/courtage/utils/resolveSourceSegmentForOptim"
import { derniereMesureConnue } from "@modules/courtage/utils/optimTurpeReserveFraicheur"
import {
    afficherCycleDeVieV1,
    lignesCycleDeVieV1,
    routeV2Query,
    titrePageV1,
} from "@modules/courtage/utils/optimturpe-v2.acces"
import {
    FIN_DE_VIE_V1,
    FIN_SUPPORT_V1,
} from "@modules/courtage/constants/optim-turpe-v2"

// ================================
// INTERRUPTEURS
// ================================
// Le cycle de vie de la V1 annonce la V2 : il ne se montre qu'aux sessions
// armées pour la V2 (défaut fermé tant que l'interrupteur n'est pas chargé).
const interrupteurs = useInterrupteursStore()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Optimisation contractuelle",
            // Q29, AV1 (vague wording du 14/10/2026).
            echecV1: {
                titre: "Étude impossible dans l'ancienne version",
                texte: "La nouvelle version indique ce qui manque et, quand c'est possible, étudie la période disponible.",
            },
            selectorLabel: "Compteur",
            noSynthesis:
                "Aucune synthèse disponible. Lancez une première analyse pour commencer.",
            startSynthesis: "Lancer une synthèse",
            warningP3:
                "Le segment P3 peut nécessiter un contrôle métier supplémentaire.",
            noEligibleMeter:
                "Aucun compteur éligible (C5/C4/C2/P4/P3/P1) n’est disponible.",
            noMeter:
                "Ce bâtiment ne contient pas de compteur. Ajoutez un PDL pour lancer l’optimisation.",
            addPdl: "Ajouter un PDL",
            lastUpdate: "Dernière mise à jour",
            // Décision A13 : les deux dates sont écrites DANS la page V1, à côté de
            // ses métadonnées de version, et non en bannière (règle de Mathieu :
            // un retard, une fin de vie, une réserve se disent dans la donnée).
            // Le texte du bloc vient de `lignesCycleDeVieV1` (maquette mq-v1,
            // D30) : dates JJ/MM/AAAA, aucune promesse de reprise.
            taskTrackingUnavailable:
                "L’analyse a été lancée, mais le suivi de tâche est indisponible. Veuillez rafraîchir la page dans quelques instants.",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const buildingStore = useBuildingStore()
const sourceStore = useSourceStore()
const taskStore = useTaskStore()
const { tasks: taskStoreTasks } = storeToRefs(taskStore)
const optimTurpeStore = useOptimTurpeStore()
const { downloadOptimTurpeReport } = useOptimTurpeDownloadReport()

const { isFetchingBuilding, isFetchingBuildingSources } =
    storeToRefs(buildingStore)

// ================================
// STATE
// ================================
const isAddPdlDialogVisible = ref(false)

/** `task_id` de la dernière synthèse lancée depuis cette page (pour état loading du bouton). */
const optimTurpeSynthesisFollowTaskId = ref<string | null>(null)

// ================================
// COMPUTED
// ================================
const getCurrentAnalyse = computed(() => optimTurpeStore.getCurrentAnalyse)
/** Le texte du bloc de fin de vie (maquette mq-v1, D30). */
const getCycleDeVieV1 = computed(() =>
    lignesCycleDeVieV1(FIN_SUPPORT_V1, FIN_DE_VIE_V1)
)
/** La V2 du même bâtiment, sur le même compteur (maquette mq-v1, D30). */
const getRouteV2 = computed(() => ({
    name: "PageBuildingOptimTurpeV2",
    params: { _id_building: String(route.params._id_building) },
    query: routeV2Query(optimTurpeStore.currentSourceId),
}))

const getErrorMessage = computed(() => {
    if (optimTurpeStore.analyseErrorMessage) {
        return optimTurpeStore.analyseErrorMessage
    }
    return optimTurpeStore.getLastFailureMessage
})

const getHasAnyPdlMeter = computed(
    () =>
        buildingStore.getMeters.filter(
            (meter) => meter.type === SOURCES_TYPES.PDL
        ).length > 0
)

const getSelectedSourceId = computed({
    get: () => optimTurpeStore.currentSourceId,
    set: (value: string) => {
        onSelectSource(value)
    },
})

const getSourceOptions = computed(() =>
    optimTurpeStore.getEligibleMeters.map((meter) => ({
        name: `${getSourceSegmentForOptim(meter) || "-"} - ${formatIdSource(meter.idsource, meter.idtype)}`,
        value: meter._id_source,
    }))
)

const getIsLoading = computed(
    () =>
        optimTurpeStore.isFetchingAnalyse ||
        isFetchingBuilding.value ||
        isFetchingBuildingSources.value
)

/**
 * Bouton « Lancer une synthèse » : chargement pendant l’API et tant que la tâche suivie n’est pas terminée.
 * Utilise `taskStoreTasks` (storeToRefs) pour que le computed se recalcule quand la tâche est mise à jour ou retirée du store.
 */
const getIsStartSynthesisLoading = computed(() => {
    const taskId = optimTurpeSynthesisFollowTaskId.value
    if (!taskId) return false
    const task = taskStoreTasks.value.find((t) => t.task_id === taskId)
    if (!task) return false
    return (
        task.state !== TASK_STATE.SUCCESS && task.state !== TASK_STATE.FAILURE
    )
})

const getLastUpdate = computed(() =>
    getCurrentAnalyse.value?.createdAt
        ? formatDate(getCurrentAnalyse.value.createdAt, "DD/MM/YYYY HH:mm")
        : ""
)

/**
 * Dernière mesure connue du compteur sélectionné (DEV-1621), servie à la carte
 * résultat pour qu'elle puisse poser sa réserve de collecte. `null` quand on ne
 * sait pas : la carte n'affirme alors rien sur la fraîcheur.
 */
const getDerniereMesureCompteur = computed(() =>
    derniereMesureConnue(optimTurpeStore.getCurrentSource?.monitoring?.lastdate)
)

/** Segment réel (ex. P3) pour l’alerte, y compris si seul `informations.pdl` est renseigné. */
const getCurrentSourceSegment = computed(() => {
    const src = optimTurpeStore.getCurrentSource
    return src ? getSourceSegmentForOptim(src) : ""
})

// ================================
// INTERACTIONS
// ================================
/**
 * Identifiant du premier compteur PDL dans l’ordre des compteurs du bâtiment (`getMeters`).
 */
function resolveFirstPdlSourceIdFromBuilding(): string {
    const firstPdl = buildingStore.getMeters.find(
        (meter) => meter.type === SOURCES_TYPES.PDL
    )
    return firstPdl?._id_source ?? ""
}

/**
 * Aligne le store sur la source à afficher : priorité au `?idsource=` de l'URL
 * (deep-link depuis le dashboard, sélection utilisateur), fallback sur le 1er PDL
 * du bâtiment uniquement si l'URL est absente ou pointe sur une source non éligible.
 * No-op si le store est déjà aligné — ce qui empêche le watcher de re-écraser un
 * choix utilisateur lors d'une re-évaluation de `getMeters`.
 */
async function syncSourceFromRoute(): Promise<void> {
    const querySourceId =
        typeof route.query.idsource === "string" ? route.query.idsource : ""
    const isQuerySourceEligible =
        querySourceId !== "" &&
        optimTurpeStore.getEligibleMeters.some(
            (meter) => meter._id_source === querySourceId
        )
    const sourceId = isQuerySourceEligible
        ? querySourceId
        : resolveFirstPdlSourceIdFromBuilding()
    if (!sourceId) return

    if (optimTurpeStore.currentSourceId === sourceId) return

    optimTurpeStore.setCurrentSourceId(sourceId)
    if (querySourceId !== sourceId) {
        await router.replace({
            query: {
                ...route.query,
                idsource: sourceId,
            },
        })
    }
    await optimTurpeStore.fetchAnalyseBySource(sourceId)
}

async function onSelectSource(sourceId: string): Promise<void> {
    if (!sourceId || sourceId === optimTurpeStore.currentSourceId) return

    optimTurpeStore.setCurrentSourceId(sourceId)
    await router.replace({
        query: {
            ...route.query,
            idsource: sourceId,
        },
    })
    await optimTurpeStore.fetchAnalyseBySource(sourceId)
}

async function onClickStartSynthesis(): Promise<void> {
    if (!optimTurpeStore.currentSourceId || !buildingStore.building?._id) return
    if (getIsStartSynthesisLoading.value) return

    optimTurpeStore.setAnalyseErrorMessage(null)
    optimTurpeSynthesisFollowTaskId.value = null

    try {
        const { task_id, event_name } =
            await optimTurpeStore.createOptimAnalyse({
                sourceId: optimTurpeStore.currentSourceId,
                buildingId: buildingStore.building._id,
                dateFrom: moment().subtract(1, "year").format("YYYY-MM-DD"),
                dateTo: moment().format("YYYY-MM-DD"),
            })

        if (!task_id || !event_name) {
            optimTurpeStore.setAnalyseErrorMessage(t("taskTrackingUnavailable"))
            return
        }

        optimTurpeSynthesisFollowTaskId.value = task_id

        taskStore.followEvent(task_id, event_name, {
            taskname: "analyse.optimturpe",
            taskToastTitle: "Analyse optimisation TURPE en cours...",
            taskToastSuccessMessage: "Analyse optimisation TURPE terminée.",
            taskToastFailureMessage: "Échec de l’analyse optimisation TURPE.",
            notification: true,
            autoDelete: true,
            onSuccess: async () => {
                optimTurpeSynthesisFollowTaskId.value = null
                await optimTurpeStore.fetchAnalyseBySource(
                    optimTurpeStore.currentSourceId
                )
            },
            onError: () => {
                optimTurpeSynthesisFollowTaskId.value = null
            },
        })

        // Tracking V1 (post-V0) : event courtage typé. `re_analysis` reflète
        // l'état AVANT l'API call (les analyses du store sont encore celles d'avant).
        const currentSource = optimTurpeStore.getCurrentSource
        const segmentRaw = currentSource
            ? getSourceSegmentForOptim(currentSource)
            : ""
        track({
            event: "Optim Analysis Started",
            payload: {
                building_id: buildingStore.building._id,
                source_id: optimTurpeStore.currentSourceId,
                source_segment: segmentRaw as OptimSegment,
                re_analysis: optimTurpeStore.getAnalyses.length > 0,
            },
        })
    } catch {
        optimTurpeSynthesisFollowTaskId.value = null
    }
}

/**
 * Rapport PDF : URL valide ou lancement de la tâche de génération (legacy `useDownloadAnalyseReport`).
 */
function onClickDownloadReport(analyseId: string): void {
    const analyse = optimTurpeStore.getAnalyses.find(
        (item) => item._id === analyseId
    )
    if (!analyse) return
    void downloadOptimTurpeReport(analyse)
}

/** Téléchargement PDF depuis la carte résultat (équivalent legacy BaseContainerAnalyse). */
function onReportMainCard(): void {
    const analyse = optimTurpeStore.getCurrentAnalyse
    if (analyse?._id) {
        onClickDownloadReport(analyse._id)
    }
}

function onAddPdl(): void {
    isAddPdlDialogVisible.value = true
}

function onCancelAddPdl(): void {
    isAddPdlDialogVisible.value = false
}

/**
 * Fin du parcours d’ajout PDL : même rafraîchissement silencieux que `PageBuildingMeter` (`onSourceAddDone`).
 */
async function onDoneAddPdl(_id_source?: string): Promise<void> {
    isAddPdlDialogVisible.value = false
    const buildingId = route.params?._id_building as string | undefined
    if (!buildingId) return
    await buildingStore.fetchBuilding(buildingId, { silent: true })
    if (_id_source) {
        await router.replace({
            query: {
                ...route.query,
                idsource: _id_source,
            },
        })
        await sourceStore.fetchSourceById({
            _id_source,
            silent: true,
        })
    } else {
        await buildingStore.fetchBuildingSources({
            sourceTypes: METER_TYPES_LIST,
            silent: true,
            force: true,
        })
    }
    await syncSourceFromRoute()
}

// ================================
// LIFECYCLE / ROUTE
// ================================
watch(
    () =>
        [
            route.params._id_building,
            route.query.idsource,
            buildingStore.getMeters
                .map((m) => `${m._id_source}:${m.type}`)
                .join("|"),
        ] as const,
    async () => {
        if (!authStore.hasModuleOptimTurpe) return
        await syncSourceFromRoute()
    },
    { immediate: true }
)
</script>

<template>
    <!-- ================================ -->
    <!-- PAGE HEADER -->
    <!-- ================================ -->
    <div class="page page-building-optimisation-contractuelle">
        <HeaderPageBuilding
            :title="titrePageV1(interrupteurs.optimturpeV2Arme, t('title'))"
        />

        <!-- ================================ -->
        <!-- MAIN CONTENT -->
        <!-- ================================ -->
        <div v-if="getIsLoading" class="flex flex-col gap-6">
            <BaseSkeleton width="100%" height="40rem" />
            <BaseSkeleton width="100%" height="40rem" />
        </div>
        <div v-else class="flex flex-col gap-6">
            <div v-if="getSourceOptions.length > 0" class="flex flex-col gap-4">
                <div class="flex justify-between items-end gap-4 flex-wrap">
                    <div class="flex flex-col gap-2 min-w-72">
                        <p class="text-xs text-primary-600">
                            {{ t("selectorLabel") }}
                        </p>
                        <BaseSelect
                            v-model="getSelectedSourceId"
                            :options="getSourceOptions"
                            option-label="name"
                            option-value="value"
                        />
                    </div>
                    <BaseButton
                        v-if="!getCurrentAnalyse"
                        :label="t('startSynthesis')"
                        icon="pi pi-refresh"
                        :loading="getIsStartSynthesisLoading"
                        :disabled="getIsStartSynthesisLoading"
                        @click="onClickStartSynthesis"
                    />
                </div>

                <BaseMessage
                    v-if="getCurrentSourceSegment === 'P3'"
                    severity="warn"
                >
                    {{ t("warningP3") }}
                </BaseMessage>

                <!-- Cycle de vie de CETTE version (A13), dans la donnée : à côté
                     des métadonnées de la page, jamais en bannière. -->
                <div
                    v-if="afficherCycleDeVieV1(interrupteurs.optimturpeV2Arme)"
                    class="flex flex-col gap-0.5 text-xs text-primary-500"
                    data-testid="optim-turpe-v1-cycle-de-vie"
                >
                    <span class="font-medium text-primary-700">{{
                        getCycleDeVieV1.titre
                    }}</span>
                    <span>{{ getCycleDeVieV1.support }}</span>
                    <span>{{ getCycleDeVieV1.consultation }}</span>
                    <span>{{ getCycleDeVieV1.nouvelleEtude }}</span>
                    <router-link
                        :to="getRouteV2"
                        class="font-medium text-primary-700 underline"
                        data-testid="optim-turpe-v1-lien-v2"
                        >{{ getCycleDeVieV1.lien }}</router-link
                    >
                </div>

                <div
                    class="flex flex-wrap items-center justify-between gap-2"
                    v-if="getCurrentAnalyse"
                >
                    <p class="text-sm text-primary-700">
                        {{ t("lastUpdate") }}:
                        <span class="font-semibold text-primary-900">{{
                            getLastUpdate
                        }}</span>
                    </p>
                    <BaseButton
                        icon="pi pi-refresh"
                        size="small"
                        outlined
                        :label="t('startSynthesis')"
                        :loading="getIsStartSynthesisLoading"
                        :disabled="getIsStartSynthesisLoading"
                        @click="onClickStartSynthesis"
                    />
                </div>

                <BaseMessage v-else-if="!getErrorMessage" severity="info">
                    {{ t("noSynthesis") }}
                </BaseMessage>

                <!-- Q29, AV1 (vague wording du 14/10/2026) : quand la nouvelle
                     version est ouverte au client, un échec de l'ancienne
                     renvoie vers elle, au moment où il est le plus utile.
                     DEV-2411 : le motif servi reste dit dessous, en une
                     ligne ; sans lui, une session armée ne savait pas
                     pourquoi l'étude avait échoué. -->
                <template
                    v-if="
                        getErrorMessage &&
                        afficherCycleDeVieV1(interrupteurs.optimturpeV2Arme)
                    "
                >
                    <BaseMessage
                        severity="error"
                        data-testid="optim-turpe-v1-echec-vers-v2"
                    >
                        <p class="font-medium">{{ t("echecV1.titre") }}</p>
                        <p>{{ t("echecV1.texte") }}</p>
                        <router-link
                            :to="getRouteV2"
                            class="font-medium underline"
                            data-testid="optim-turpe-v1-echec-lien-v2"
                            >{{ getCycleDeVieV1.lien }}</router-link
                        >
                    </BaseMessage>
                    <p
                        class="text-sm text-primary-700"
                        data-testid="optim-turpe-v1-echec-motif"
                    >
                        {{ getErrorMessage }}
                    </p>
                </template>
                <BaseMessage v-else-if="getErrorMessage" severity="error">
                    {{ getErrorMessage }}
                </BaseMessage>

                <OptimTurpeCardResult
                    v-if="getCurrentAnalyse"
                    :analyse="getCurrentAnalyse"
                    :derniere-mesure-compteur="getDerniereMesureCompteur"
                    @click:report="onReportMainCard"
                />

                <OptimTurpeHistoryTable
                    v-if="optimTurpeStore.getAnalyses.length > 1"
                    :analyses="optimTurpeStore.getAnalyses"
                    @click:report="onClickDownloadReport"
                />
            </div>

            <BaseMessage
                v-else-if="!getHasAnyPdlMeter"
                class="w-fit mx-auto"
                severity="info"
                :pt="{
                    text: 'flex flex-col! gap-2',
                }"
            >
                <span>{{ t("noMeter") }}</span>

                <BaseButton
                    :label="t('addPdl')"
                    size="small"
                    class="w-fit mx-auto"
                    @click="onAddPdl"
                />
            </BaseMessage>

            <BaseMessage v-else severity="info">
                {{ t("noEligibleMeter") }}
            </BaseMessage>
        </div>

        <!-- ================================ -->
        <!-- APPEND PDL DIALOG -->
        <!-- ================================ -->
        <SourceAppendDialog
            v-model:visible="isAddPdlDialogVisible"
            from-state="pdl"
            :allowed-source-types="['pdl']"
            :building-id="buildingStore.building?._id"
            :address="buildingStore.building?.address ?? null"
            @cancel="onCancelAddPdl"
            @done="onDoneAddPdl"
        />
    </div>
</template>
